import dagre from '@dagrejs/dagre';

const NODE_WIDTH = 160;
const NODE_HEIGHT = 56;
const GATE_SIZE = 48;

/**
 * Convert a ProcessDefinition JSON into React Flow nodes + edges
 * laid out via dagre (LR direction).
 */
export function layoutProcessDefinition(definition) {
  if (!definition) return { nodes: [], edges: [] };

  const g = new dagre.graphlib.Graph({ compound: true });
  g.setGraph({
    rankdir: 'LR',
    ranksep: 160,
    nodesep: 50,
    edgesep: 24,
    marginx: 50,
    marginy: 50,
  });
  g.setDefaultEdgeLabel(() => ({}));

  const nodeMap = {};
  const gateMap = {};

  (definition.nodes || []).forEach((n) => {
    const isGate = false;
    nodeMap[n.node_id] = n;
    g.setNode(n.node_id, {
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      label: n.name,
      isGate,
    });
  });

  (definition.gates || []).forEach((gate) => {
    gateMap[gate.gate_id] = gate;
    g.setNode(gate.gate_id, {
      width: GATE_SIZE,
      height: GATE_SIZE,
      label: gate.gate_id,
      isGate: true,
    });
    // Wire upstream nodes into the gate so the AB map shows the full
    // input -> gate -> output flow even when the process has no explicit
    // synapses (gates fully express the wiring).
    (gate.input_nodes || []).forEach((inId) => {
      if (g.hasNode(inId)) {
        g.setEdge(inId, gate.gate_id);
      }
    });
    (gate.output_nodes || []).forEach((outId) => {
      if (g.hasNode(outId)) {
        g.setEdge(gate.gate_id, outId);
      }
    });
  });

  (definition.synapses || []).forEach((syn) => {
    if (g.hasNode(syn.source_node) && g.hasNode(syn.target_node)) {
      g.setEdge(syn.source_node, syn.target_node);
    }
  });

  dagre.layout(g);

  const rfNodes = [];
  g.nodes().forEach((id) => {
    const dagreNode = g.node(id);
    if (!dagreNode) return;

    const isGate = dagreNode.isGate;
    const procNode = nodeMap[id];
    const gateObj = gateMap[id];

    const typeLetter = isGate
      ? 'G'
      : (procNode?.execution_type === 'snn_pipeline' || procNode?.execution_type === 'runtime_pipeline')
        ? 'S'
        : procNode?.execution_type === 'llm'
          ? 'L'
          : procNode?.execution_type === 'case_extractor'
            ? 'E'
            : procNode?.execution_type === 'chain_reasoner'
              ? 'C'
              : procNode?.execution_type === 'python'
                ? 'P'
                : procNode?.execution_type === 'http'
                  ? 'H'
                  : '?';

    rfNodes.push({
      id,
      type: 'abExecutionNode',
      position: {
        x: dagreNode.x - dagreNode.width / 2,
        y: dagreNode.y - dagreNode.height / 2,
      },
      data: {
        label: isGate ? (gateObj?.gate_type || 'AND') : (procNode?.name || id),
        typeLetter,
        nodeId: id,
        executionType: isGate ? 'gate' : procNode?.execution_type,
        isGate,
        status: 'pending',
      },
      style: {
        width: isGate ? GATE_SIZE : NODE_WIDTH,
        height: isGate ? GATE_SIZE : NODE_HEIGHT,
      },
    });
  });

  const rfEdges = [];
  g.edges().forEach((e) => {
    rfEdges.push({
      id: `${e.v}->${e.w}`,
      source: e.v,
      target: e.w,
      type: 'smoothstep',
      animated: false,
      style: { stroke: '#94a3b8', strokeWidth: 1.5 },
      markerEnd: { type: 'arrowclosed', color: '#94a3b8', width: 12, height: 12 },
    });
  });

  return { nodes: rfNodes, edges: rfEdges };
}

/**
 * Derive phase groupings from the definition's gates.
 * Returns array of { label, nodeIds, bounds }.
 */
export function computePhaseGroups(definition, rfNodes) {
  if (!definition?.gates?.length) return [];

  const nodePositions = {};
  rfNodes.forEach((n) => {
    nodePositions[n.id] = n.position;
  });

  const groups = [];
  definition.gates.forEach((gate, idx) => {
    const memberIds = [...(gate.input_nodes || [])];
    const positions = memberIds
      .map((id) => nodePositions[id])
      .filter(Boolean);
    if (!positions.length) return;

    const minX = Math.min(...positions.map((p) => p.x)) - 20;
    const minY = Math.min(...positions.map((p) => p.y)) - 20;
    const maxX = Math.max(...positions.map((p) => p.x)) + NODE_WIDTH + 20;
    const maxY = Math.max(...positions.map((p) => p.y)) + NODE_HEIGHT + 20;

    groups.push({
      label: `Phase ${idx + 1}`,
      nodeIds: memberIds,
      bounds: { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
    });
  });

  return groups;
}

/**
 * Update node statuses in place given execution state.
 * Gates are tracked separately via fired_gates / waiting_gates,
 * so we derive their status from those fields.
 */
export function applyExecutionStatus(rfNodes, executionState) {
  if (!executionState) return rfNodes;

  const completed = new Set(executionState.completed_nodes || []);
  const active = new Set(executionState.active_nodes || []);
  const failed = new Set(executionState.failed_nodes || []);
  const skipped = new Set(executionState.skipped_nodes || []);
  const firedGates = new Set(executionState.fired_gates || []);
  const waitingGates = executionState.waiting_gates || {};

  return rfNodes.map((n) => {
    let status = 'pending';
    if (completed.has(n.id)) status = 'completed';
    else if (active.has(n.id)) status = 'active';
    else if (failed.has(n.id)) status = 'failed';
    else if (skipped.has(n.id)) status = 'skipped';
    else if (firedGates.has(n.id)) status = 'completed';
    else if (waitingGates[n.id]?.length > 0) status = 'active';

    return {
      ...n,
      data: { ...n.data, status },
    };
  });
}

/**
 * Update edge styles based on node statuses.
 */
export function applyEdgeStatus(rfEdges, rfNodes) {
  const statusMap = {};
  rfNodes.forEach((n) => { statusMap[n.id] = n.data.status; });

  return rfEdges.map((e) => {
    const srcDone = statusMap[e.source] === 'completed';
    const tgtActive = statusMap[e.target] === 'active';
    const tgtDone = statusMap[e.target] === 'completed';

    let stroke = '#94a3b8';
    let animated = false;
    let strokeWidth = 1.5;

    if (srcDone && tgtDone) {
      stroke = '#16a34a';
      strokeWidth = 2;
    } else if (srcDone && tgtActive) {
      stroke = '#22c55e';
      animated = true;
      strokeWidth = 2;
    }

    return {
      ...e,
      animated,
      style: { ...e.style, stroke, strokeWidth },
      markerEnd: { ...e.markerEnd, color: stroke },
    };
  });
}
